import { useI18n } from '../../../i18n';
// ============================================
// RevenueOverTimeChart - Stacked bar: This Year (bottom) + Last Year (top)
// ============================================

import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { useState } from 'react';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

export interface RevenueOverTimeData {
  month: string;
  thisYear: number;
  lastYear: number;
}

type ChartData = RevenueOverTimeData & { gap: number };

interface Props {
  data: RevenueOverTimeData[];
  className?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  const { t } = useI18n();
  const money = useMoney();
  // Tối đa 2 số thập phân — bản cũ tự ghép chuỗi bằng % 1000 nên số float
  // lọt nguyên đuôi thập phân vào tooltip ($4,213.44999999999982).
  // Helper nằm trong component vì `useMoney()` là hook.
  const fmt = (v: number): string =>
    money.format(v, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  if (!active || !payload?.length) return null;
  const thisYear = payload.find((p) => p.name === 'This Year');
  const lastYear = payload.find((p) => p.name === 'Last Year');
  return (
    <div className="rounded-xl bg-white px-4 py-3 shadow-xl ring-1 ring-black/10 text-xs min-w-[170px]">
      <p className="mb-2 font-bold text-slate-800">{label}</p>
      {thisYear && (
        <p className="font-semibold text-[#0057D9]">{t('thisYear')}: {fmt(thisYear.value)}</p>
      )}
      {lastYear && (
        <p className="mt-0.5 font-medium text-[#93C5FD]">{t('lastYear')}: {fmt(lastYear.value)}</p>
      )}
    </div>
  );
}

export function RevenueOverTimeChart({ data, className }: Props) {
  const { t } = useI18n();
  const money = useMoney();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Khoảng trắng giữa 2 khúc cột tính THEO TỈ LỆ dữ liệu (~1.5% cột cao nhất),
  // thay cho hằng số 800 cũ — doanh thu nhỏ thì 800 chiếm cả 20% cột.
  const maxStack = Math.max(...data.map((d) => d.thisYear + d.lastYear), 0);
  const gap = maxStack > 0 ? maxStack * 0.015 : 0;
  const chartData: ChartData[] = data.map((d) => ({ ...d, gap }));

  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-sm', className)}>
      <h3 className="mb-1 text-base font-bold text-[#191B23]">{t('revenueOverTime')}</h3>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-full bg-[#0057D9]" />
          <span className="text-xs text-slate-500">{t('thisYear')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-full bg-[#93C5FD]" />
          <span className="text-xs text-slate-500">{t('lastYear')}</span>
        </div>
      </div>

      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 5, left: 0, bottom: 0 }}
            barSize={36}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontWeight: 600, fill: '#94A3B8' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              tickFormatter={(v) => money.compact(v)}
              width={48}
            />
            {/* Thẻ thông tin nổi LÊN TRÊN cột đang hover: neo y phía trên vùng
                vẽ (allowEscapeViewBox), x bám theo cột; offset âm ~nửa bề rộng
                thẻ (min-w 170px) để thẻ canh giữa cột thay vì lệch phải. */}
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              position={{ y: -88 }}
              allowEscapeViewBox={{ x: false, y: true }}
              offset={-85}
            />
            {/* This Year - bottom of stack */}
            <Bar
              dataKey="thisYear"
              name="This Year"
              stackId="a"
              radius={[0, 0, 3, 3]}
              onMouseEnter={(_, index) => setActiveIndex(index)}
            >
              {chartData.map((_, index) => (
                <Cell key={index} fill={activeIndex === index ? '#0288D1' : '#0288D1'} />
              ))}
            </Bar>
            {/* White gap between stacks */}
            <Bar
              dataKey="gap"
              name=""
              stackId="a"
              fill="white"
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
            />
            {/* Last Year - top of stack */}
            <Bar
              dataKey="lastYear"
              name="Last Year"
              stackId="a"
              radius={[4, 4, 0, 0]}
              onMouseEnter={(_, index) => setActiveIndex(index)}
            >
              {chartData.map((_, index) => (
                <Cell key={index} fill={activeIndex === index ? '#ACD9F0' : '#ACD9F0'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
